import { put, takeLatest, call } from 'redux-saga/effects';
import * as actions from '../ducks/admin';
import { toastify } from '../ducks/toast';
import { request, finish } from './helpers';
import { setAdminToken, clearAdminToken } from '../utils/auth';
import { downloadCsv, date } from '../utils/format';

//login is the WiserNotify admin panel login: { status, token } or { status: 'error', message }
function* adminLogin(action) {
  const res = yield* request('POST', '/aff/admin/login', action.payload);
  if (res.status === 'success' && res.token) yield call(setAdminToken, res.token);
  yield* finish('adminLogin', { ...res, m: res.m || res.message }, { toastSuccess: false });
}

function* adminLogout() {
  yield call(clearAdminToken);
}

function* load(url, payload, success) {
  const res = yield* request('POST', url, payload);
  if (res.status === 'success') yield put(success(res.data));
  else if (res.status !== 'Unauthorized') yield put(toastify({ type: 'error', msg: res.m }));
  return res;
}

function* getAffiliates(action) {
  yield* load('/aff/admin/affiliate/list', action.payload, actions.getAffiliatesSuccess);
}

function* getAffiliateDetail(action) {
  yield* load('/aff/admin/affiliate/detail', action.payload, actions.getAffiliateDetailSuccess);
}

function* getWithdrawals(action) {
  yield* load('/aff/admin/affiliate/withdrawals', action.payload, actions.getWithdrawalsSuccess);
}

function* getCommissions(action) {
  yield* load('/aff/admin/affiliate/commissions', action.payload, actions.getCommissionsSuccess);
}

//actions: run, toast, then let the page reload what it shows (flow name = action type)
function* mutate(name, url, payload) {
  const res = yield* request('POST', url, payload);
  yield* finish(name, res);
}

function* updateAffiliateStatus(action) { yield* mutate('updateAffiliateStatus', '/aff/admin/affiliate/status', action.payload); }
function* updateWithdrawal(action) { yield* mutate('updateWithdrawal', '/aff/admin/affiliate/withdrawal/update', action.payload); }
function* reverseCommission(action) { yield* mutate('reverseCommission', '/aff/admin/affiliate/commission/reverse', action.payload); }

//export the current filter (up to 1000 rows) as CSV
function* exportCsv(action) {
  const { type, filter } = action.payload;
  if (type === 'affiliates') {
    const res = yield* request('POST', '/aff/admin/affiliate/list', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'wn-affiliates.csv',
      ['ti', 'Email', 'Name', 'Code', 'Account', 'Status', 'Verified', 'PayPal', 'Website', 'Country', 'Joined', 'Clicks', 'Sign ups', 'Paid customers', 'Earned', 'Refunded', 'Pending', 'Available', 'Paid out', 'In process', 'Balance'],
      res.data.rows.map((a) => [a.ti, a.e, a.name, a.code, a.src === 'migrated' ? 'WiserNotify user' : 'Registered', a.st, a.ivrfy ? 'Yes' : 'No', a.ppl, a.web, a.cn, date(a.cdt), a.clicks, a.signups, a.paidSignups, a.earned, a.refunded, a.pending, a.available, a.paidOut, a.inProcess, a.balance]));
  } else if (type === 'withdrawals') {
    const res = yield* request('POST', '/aff/admin/affiliate/withdrawals', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'wn-affiliate-withdrawals.csv',
      ['Requested', 'Affiliate email', 'Affiliate name', 'Amount (USD)', 'PayPal', 'Status', 'Transaction', 'Remark', 'Updated', 'By'],
      res.data.rows.map((w) => [date(w.cdt), w.affEmail, w.affName, w.amt, w.ppl, w.st, w.txnref, w.rmk, date(w.udt), w.aby]));
  } else if (type === 'commissions') {
    const res = yield* request('POST', '/aff/admin/affiliate/commissions', { ...filter, page: 1, pageSize: 1000 });
    if (res.status !== 'success') return yield put(toastify({ type: 'error', msg: res.m }));
    yield call(downloadCsv, 'wn-affiliate-commissions.csv',
      ['Payment date', 'Affiliate', 'Customer', 'Plan', 'Type', 'Paid (USD)', 'Rate %', 'Commission', 'Reversed', 'Net', 'Available from', 'Status', 'Remark', 'Invoice'],
      res.data.rows.map((c) => [date(c.paydt), c.affEmail, c.customer, c.plan, c.typ, c.paid, c.rate, c.commission, c.reversed, c.net, date(c.availdt), c.status, c.rmk, c.srcid]));
  }
}

export default function* adminSaga() {
  yield takeLatest(actions.adminLogin.type, adminLogin);
  yield takeLatest(actions.adminLogout.type, adminLogout);
  yield takeLatest(actions.getAffiliates.type, getAffiliates);
  yield takeLatest(actions.getAffiliateDetail.type, getAffiliateDetail);
  yield takeLatest(actions.updateAffiliateStatus.type, updateAffiliateStatus);
  yield takeLatest(actions.getWithdrawals.type, getWithdrawals);
  yield takeLatest(actions.updateWithdrawal.type, updateWithdrawal);
  yield takeLatest(actions.getCommissions.type, getCommissions);
  yield takeLatest(actions.reverseCommission.type, reverseCommission);
  yield takeLatest(actions.exportCsv.type, exportCsv);
}
